import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { getAllLocalizations } from "../api/localizationApi";
import { getLocales } from "../api/spaceApi";

const LocalizationContext = createContext();
const LOCALE_STORAGE_KEY = "cms_locale";

export const LocalizationProvider = ({ children }) => {
  const { activeSpace, isAuthenticated, signInError } = useAuth();
  const spaceId = (activeSpace?._isAdmin || activeSpace?._isFinance) ? null : (activeSpace?.spaceId ?? null);

  const [locale, setLocaleState] = useState(
    () => localStorage.getItem(LOCALE_STORAGE_KEY) || "en_us"
  );
  const [translations, setTranslations] = useState({});
  const [availableLocales, setAvailableLocales] = useState(() => {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY) || "en_us";
    return [stored];
  });

  const setLocale = (newLocale) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
  };

  const updateTranslation = (key, localeId, newValue) => {
    setTranslations((prev) => ({
      ...prev,
      [key]: { ...prev[key], [localeId]: newValue },
    }));
  };

  useEffect(() => {
    // isAuthenticated guards against a stale spaceId in localStorage outliving a dead
    // session. signInError covers a stale MSAL account keeping isAuthenticated true after exchange fails.
    if (!isAuthenticated || signInError || !spaceId) return;

    // Reset stale data before fetching for the newly selected space
    setTranslations({});

    (async () => {
      try {
        const data = await getAllLocalizations();
        const map = {};
        data.forEach((item) => {
          item.values.forEach((v) => {
            if (!map[item.key]) map[item.key] = {};
            map[item.key][v.localeId] = v.value;
          });
        });
        setTranslations(map);
      } catch (err) {
        console.error("Failed to load localizations", err);
      }
    })();

    (async () => {
      try {
        const langs = await getLocales();
        // API now returns [{ localeId, displayName }] — extract IDs to keep consumers working
        setAvailableLocales(langs.map((l) => l.localeId));
      } catch (err) {
        console.error("Failed to load locales", err);
      }
    })();
  }, [spaceId, isAuthenticated, signInError]); // re-runs automatically whenever the active space changes

  const t = (key, fallback = "") => translations[key]?.[locale] || fallback;

  return (
    <LocalizationContext.Provider value={{ locale, setLocale, t, updateTranslation, availableLocales }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);
